/**
 * The pixels a value was read from.
 *
 * Two honest limits are surfaced rather than hidden. A row parsed from a file that has
 * no printed page has no crop at all, and the server says so with a 409 — that is not an
 * error, it means the value is exact. And because `Provenance.bbox` is still `None` for
 * loss rows, a crop today is the whole page; it narrows on its own the moment the parser
 * emits cell boxes.
 */
import { useEffect, useState } from "react";
import { ApiError, imageObjectUrl } from "../lib/api";
import { Loading } from "./ui";

export function Crop({ rowId }: { rowId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let created: string | null = null;
    setUrl(null);
    setNote(null);
    setFailed(false);

    imageObjectUrl(`/v1/rows/${rowId}/crop`)
      .then((objectUrl) => {
        if (!live) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        created = objectUrl;
        setUrl(objectUrl);
      })
      .catch((error: unknown) => {
        if (!live) return;
        // 409 is the "no printed page" case, and its message is the explanation.
        setFailed(!(error instanceof ApiError && error.status === 409));
        setNote(error instanceof Error ? error.message : String(error));
      });

    return () => {
      live = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [rowId]);

  if (note) {
    return (
      <p
        className={`px-4 py-3 text-[12px] leading-relaxed ${failed ? "text-crit" : "text-dim"}`}
      >
        <span className="font-mono text-faint">&gt;</span> {note}
      </p>
    );
  }
  if (!url) return <Loading what="the crop" />;

  return (
    <div className="bg-ground-soft p-3">
      <img
        src={url}
        alt="The page region this row was read from"
        className="max-h-[280px] w-full border border-hair object-contain"
      />
      <p className="mt-2 font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
        &gt; page-level crop — narrows to the cell once the parser emits boxes
      </p>
    </div>
  );
}
